import { VettingService } from './../../../../services/vetting/vetting.service';
import { BaseWorkflowActivityAssignmentViewModel } from "../base-workflow-activity-assignment";
import { autoinject, computedFrom, observable } from "aurelia-framework";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { I18N } from "aurelia-i18n";
import { WorkflowAssignmentsService } from "../../../../services/workflow/workflow-assignments-service";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { Router } from "aurelia-router";
import { CacheClientService } from "@rightship-tech/common-ui/services/basic/client-caching.service";
import { ClientSideValidator, EntityValidationsService } from "../../../../client-validation-system/validations-service";
import { ValidationActivation, ValidationTypeEnum } from "../../../../client-validation-system/validations.models";
import { type OnboardingConfigModel, type OnboardingLandingPage } from "../../../../models/onboarding/onboarding.models";
import { Reflection } from "../../../../utilities/reflection";
import { ClipboardCopyService } from "../../../../utilities/clipboard-copy-service";
import { RightShipWorkflowStateEnum } from "../../../../models/workflow/rightship.workflow.models";
import { LookupValue } from "../../../../models/lookups/lookup.models";
import { AttachmentClientService } from "../../../../services/attachment/attachments-client.service";
import { EntityAttachmentModel } from "../../../../models/attachment/attachment.models";
import { QueriesClientService } from "../../../../services/queries/queries-client.services";
import { EmailRecipientTypeEnum } from '../../../../models/vetting/vetting.models';
import * as Enumerable from "linq";
import { RightShipOnboardingService } from '../../../../services/rightship-onboarding/rightship-onboarding.service';

@autoinject()
export class ActivityAssignment extends BaseWorkflowActivityAssignmentViewModel {

    public isAssignmentCancelled: boolean;
    public isWorkflowStateCancelled: boolean;
    public recipients: any[] = [];
    public acceptableDayOnlyReasons: LookupValue[] = [];
    public acceptableDayOnlyReason: number;
    public showRecipients: boolean = false;
    public canResend: boolean = true;
    public files: File[] = [];
    public previousAttachments: EntityAttachmentModel[] = [];
    public vetId: number;
    public vettingContactEmails: { email: string, emailRecipientTypeId: number, isShipManager: boolean }[] = [];
    public vettingContactMessage: string;
    public remarksText: string;
    public currentOnboardingRequestGuid: string;
    validator: ClientSideValidator;

    @observable()
    public showVettingContactView: boolean;
    public disableAutoComplete: boolean = true;

    constructor(
        protected dialogClientService: DialogClientService,
        protected i18N: I18N,
        protected router: Router,
        protected cacheClient: CacheClientService,
        protected userClaimsService: UserClaimsService,
        protected notificationClientService: NotificationClientService,
        protected workflowAssignmentsService: WorkflowAssignmentsService,
        private attachmnetService: AttachmentClientService,
        private entityValidationsService: EntityValidationsService,
        private clipboardCopyService: ClipboardCopyService,
        private queryService: QueriesClientService,
        protected vetClientService: VettingService,
        private rightShipOnboardingService: RightShipOnboardingService) {
        super(dialogClientService, i18N, workflowAssignmentsService, vetClientService);
    }

    @computedFrom(Reflection.getPropertyPath<ActivityAssignment>(z => z.entityTypeId),
        Reflection.getPropertyPath<ActivityAssignment>(z => z.currentOnboardingRequestGuid),
        Reflection.getPropertyPath<ActivityAssignment>(z => z.currentOnboardingConfig))
    get hasRenewalLink(): boolean {
        if (!this.assignment.isCurrentAssignment) {
            return false;
        }
        return this.formUrl != null;
    }

    @computedFrom(Reflection.getPropertyPath<ActivityAssignment>(z => z._model.item))
    get currentOnboardingLandingPage(): OnboardingLandingPage {
        return this.item ? this.item.currentOnboardingLandingPage : null;
    }

    @computedFrom(Reflection.getPropertyPath<ActivityAssignment>(z => z._model.item))
    get currentOnboardingConfig(): OnboardingConfigModel {
        return this.item ? this.item.currentOnboardingConfig : null;
    }

    @computedFrom(Reflection.getPropertyPath<ActivityAssignment>(z => z.currentOnboardingRequestGuid), Reflection.getPropertyPath<ActivityAssignment>(z => z.currentOnboardingConfig))
    get formUrl(): string {
        if (this.currentOnboardingRequestGuid && this.currentOnboardingConfig) {
            return `${window["origin"]}/OnboardingClient/${this.currentOnboardingConfig.landingPages[0].guid}?onboardingRequestGuid=${this.currentOnboardingRequestGuid}`;
        }
        return null;
    }

    @computedFrom('_model')
    public get isHQ() {
        return this._model && this._model.entityTypeId == 963006;
    }

    @computedFrom('_model')
    public get isNeedMoreInfo() {
        return this.assignment.meta && JSON.parse(this.assignment.meta).needMoreInfo == true;
    }

    @computedFrom('_model')
    public get isHayPoint() {
        return this._model && this._model.entityTypeId == 876775;
    }

    @computedFrom('isNeedMoreInfo')
    public get needMoreInfoReason() {
        if (this.isNeedMoreInfo) {

            const meta = JSON.parse(this.assignment.meta);

            if (meta.acceptableDayOnlyReason) {
                return this.acceptableDayOnlyReasons.filter(x => x.id == meta.acceptableDayOnlyReason)[0].name;
            }

        }

        return null;
    }
    @computedFrom('_model')
    public get isPreviousNeeedMoreInfo() {
        const previousAssignments = this.workflowState.workflowAssignments.filter(x => x.stepNumber < this.assignment.stepNumber);
        const previousAssignmentStepNumber = Math.max(...previousAssignments.map(x => x.stepNumber));
        const previousAssignment = previousAssignments.filter(x => x.stepNumber == previousAssignmentStepNumber)[0];

        return previousAssignment?.meta && JSON.parse(previousAssignment.meta).needMoreInfo == true;
    }


    async onActivated() {
        this.vetId = this._model.item.vetId;
        this.assignment.assignmentState.showStatus = false;
        this.assignment.assignmentState.isSystemAssignment = true;
        const meta = this.assignment.meta ? JSON.parse(this.assignment.meta) : {};

        if (this.assignment.isCurrentAssignment) {
            this.getAssignmentFields()

            const shipManagerEmails = this.correspondenceEmails.filter(x => x.isShipManager).map(x => x.email)
            this.recipients.forEach(x => {
                x.emailRecipientType = shipManagerEmails.includes(x.recipientEmail) ? EmailRecipientTypeEnum.To : EmailRecipientTypeEnum.Bcc
            })

        }

        this.isAssignmentCancelled = meta && (meta.cancelledBy || meta.cancelledBy == "");
        if (this.isAssignmentCancelled) {
            this.assignment.assignmentState.actionTakerAnonText = "Form has been Cancelled" + (this.workflowState.workflowCancellationNotes != this.assignment.remarks ? (', ' + this.workflowState.workflowCancellationNotes) : '');
        } else if (!this.isUpcomingAssignment && !this.assignment.isCurrentAssignment) {
            this.assignment.assignmentState.actionTakerAnonText = "Form has been submitted";
        } else {
            this.assignment.assignmentState.actionTakerAnonText = "Waiting for Ship Manager's Response";
        }

        this.canResend = this.item && this.item.hideResend != true;

        const rulesHolderEmails = {
            activation: ValidationActivation.Always,
            entityName: "Recipients Emails",
            isCollection: true,
            nestedRulesHolder: [],
            rules: [{
                errorMessage: "Email can not be empty",
                parameters: null, property: 'recipientEmail',
                validationType: ValidationTypeEnum.Required
            }, {
                errorMessage: "Email is not in correct format",
                parameters: null, property: 'recipientEmail',
                validationType: ValidationTypeEnum.Email
            }]
        };

        this.acceptableDayOnlyReasons = [];

        if (this.isHQ) {
            this.acceptableDayOnlyReasons.push(<LookupValue>{ name: "Requires Clarification", id: 1 });
            this.acceptableDayOnlyReasons.push(<LookupValue>{ name: "Acceptable Day Only MRR", id: 2 });
            this.acceptableDayOnlyReasons.push(<LookupValue>{ name: "No Landing", id: 3 });
        }
        else if (this.isHayPoint) {
            this.acceptableDayOnlyReasons.push(<LookupValue>{ name: "Requires Clarification", id: 1 });
            this.acceptableDayOnlyReasons.push(<LookupValue>{ name: "Conditionally Approved", id: 2 });
        }

        this.validator = this.entityValidationsService.getValidator(this.recipients, rulesHolderEmails);
        this.fetchCurrentOnboardingRequestData();
    }

    getAssignmentFields() {
        const previousAssignments = this.workflowState.workflowAssignments.filter(x => x.stepNumber < this.assignment.stepNumber);
        const previousAssignmentStepNumber = Math.max(...previousAssignments.map(x => x.stepNumber));
        const previousAssignment = previousAssignments.filter(x => x.stepNumber == previousAssignmentStepNumber)[0];
        const previousAssignmentMeta = previousAssignment ? JSON.parse(previousAssignment.meta) : null;
        const currentMeta = this.assignment && this.assignment.meta ? JSON.parse(this.assignment.meta) : {};

        this.remarksText = this.assignment?.remarks ?? previousAssignment?.remarks ?? this.remarksText;

        if (previousAssignment) {
            this.attachmnetService.getAttachments({ entityId: previousAssignment.workflowAssignmentId, entityType: EntityTypes.WorkflowAssignment }).then(x => {
                if (x && x.entityAttachments && x.entityAttachments.length > 0) {
                    this.previousAttachments = x.entityAttachments;
                }
            });
        }

        this.files = []

        this.showVettingContactView = (previousAssignmentMeta?.vettingContactEmails?.length ?? 0) > 0
        this.disableAutoComplete = previousAssignmentMeta?.disableAutoComplete ?? this.disableAutoComplete;

        this.isWorkflowStateCancelled = this.isLastClosedAssignment && this.workflowState.state == RightShipWorkflowStateEnum.Cancelled;

        this.vettingContactMessage = previousAssignmentMeta?.vettingContactMessage || currentMeta?.vettingContactMessage || null;
        this.vettingContactEmails = (previousAssignmentMeta?.vettingContactEmails || currentMeta?.vettingContactEmails || []).filter(x => !x.isShipManager);

        const workflowRecipients = this.workflowState?.workflowMeta ? JSON.parse(this.workflowState?.workflowMeta).recipients : [{ recipientEntityTypeId: null, recipientEntityId: null, recipientEmail: null }];

        this.recipients = previousAssignmentMeta?.recipients ?? workflowRecipients ?? [];
        if (this.recipients.length == 0) {
            this.recipients.push({ recipientEntityTypeId: null, recipientEntityId: null, recipientEmail: null, emailRecipientType: null });
        }

        this.acceptableDayOnlyReason = previousAssignmentMeta?.acceptableDayOnlyReason ?? this.acceptableDayOnlyReason;

    }

    @computedFrom("assignment", "assignment.meta", "isAssignmentCancelled")
    get cancelledBy() {

        if (!this.isAssignmentCancelled) return null;

        const meta = this.assignment && this.assignment.meta ? JSON.parse(this.assignment.meta) : {};

        if (meta && meta.cancelledBy) {
            return meta.cancelledBy;
        }
        else return null;
    }

    @computedFrom(Reflection.getPropertyPath<ActivityAssignment>(z => z.assignment),
        Reflection.getPropertyPath<ActivityAssignment>(z => z.assignment.meta))
    get subtaskId() {
        const meta = this.assignment && this.assignment.meta ? JSON.parse(this.assignment.meta) : {};
        return meta.subtaskId;
    }

    async resendEmail() {
        try {
            let isValid = true;
            isValid = await this.validator.validate().then(x => {
                return x.valid;
            });

            if (!isValid) {
                return;
            }
            if (this.isPreviousNeeedMoreInfo) {
                const metaParsed = this.assignment.meta ? JSON.parse(this.assignment.meta) : {};
                metaParsed.acceptableDayOnly = false;
                metaParsed.reopened = false;
                metaParsed.vettingContactEmails = this.showVettingContactView ? this.vettingContactEmails : [];
                metaParsed.vettingContactMessage = this.vettingContactMessage;
                metaParsed.disableAutoComplete = this.disableAutoComplete;
                metaParsed.recipients = this.recipients;
                metaParsed.needMoreInfo = true;
                metaParsed.includedPreviousAttachmentIds = this.previousAttachments.map(x => x.entityAttachmentId)
                metaParsed.acceptableDayOnlyReason = this.acceptableDayOnlyReason;
                metaParsed.correspondenceEmails = this.correspondenceEmails
                this.assignment.meta = JSON.stringify(metaParsed);
                return new Promise<void>((resolve, reject) => {
                    this.workflowAssignmentsService.executeAction(this.assignment.workflowStateId,
                        'rightship-review-activity',
                        'need-more-info',
                        this.assignment.workflowAssignmentId,
                        null,
                        this.remarksText,
                        this.assignment.meta,
                        this.files
                    ).then(response => {
                        if (response.errorMessage) {
                            this.notificationClientService.show("", response.errorMessage, NotificationType.Error);
                        }
                        else {
                            this.reload(response.workflowState, response.canTakeOwnership, response.canReassign);
                            this.notificationClientService.show("Need More Info (Resend Email) Submitted", "Email was resent successfully", NotificationType.Success);
                        }
                        resolve();
                    }).catch(reject);
                });
            }

            const response = await this.workflowAssignmentsService.executeAction(this.assignment.workflowStateId,
                'onboarding-submitted-activity',
                'resend-email',
                this.assignment.workflowAssignmentId,
                null,
                this.assignment.remarks,
                JSON.stringify({ recipients: this.recipients })
            );

            if (response.errorMessage) {
                this.notificationClientService.show("Error", response.errorMessage, NotificationType.Error);
            } else {
                this.notificationClientService.show("Success", "Email was resent successfully", NotificationType.Success);
            }
            this.reload(response.workflowState, response.canTakeOwnership, response.canReassign);
        } catch {
            this.notificationClientService.show("Error", "Failed to resend email", NotificationType.Error);
        }
    }

    public saveDraft() {
        return new Promise<void>((resolve, reject) => {
            this.rightShipOnboardingService.saveDraftComment(this.assignment.workflowAssignmentId, this.remarksText).then(() => {
                this.notificationClientService.show("Draft successfully saved", "Comment successfully saved. Do not forget to submit.", NotificationType.Success);
                resolve();
            })
            .catch(e => {
                this.notificationClientService.show("Draft save failed", "Please try again later.", NotificationType.Error);
                reject(e);
            });
        });
    }

    toggleShowRecipients() {
        this.showRecipients = !this.showRecipients;
        if (!this.showRecipients) {
            this.getAssignmentFields()

        }
    }

    public getEmailLabel(email) {
        const emailMatches = this.correspondenceEmails?.filter(x => x.email == email) ?? [];
        //case when both ship manager and requestor email are the same
        if (emailMatches.length > 1) {
            return emailMatches.filter(x => x.isShipManager).length > 0 ? "(Ship Manager)" : emailMatches[0]?.recipientType
        }
        else {
            return emailMatches[0]?.recipientType
        }
    }

    removeEmail(recipient: any) {
        const index = this.recipients.findIndex(x => x == recipient);
        this.recipients.splice(index, 1);
        if (this.recipients.length == 1) {
            this.recipients[0].emailRecipientType = EmailRecipientTypeEnum.To as number
        }
    }

    addEmail() {
        this.recipients.push({ recipientEntityTypeId: null, recipientEntityId: null, recipientEmail: null, emailRecipientType: null });
    }

    copyRenewalLink() {
        this.clipboardCopyService.copy(this.formUrl);
    }

    async fetchCurrentOnboardingRequestData() {

        const response = await this.queryService.getData('odata/VetTaskOnboardingRequests?$filter=(vesselApplicationFormId eq ' + this.workflowState.entityId +
            ')&$orderby=createdDate desc, onboardingRequestId desc&$select=onboardingRequestGuid,onboardingRequestId&$top=1')
        this.currentOnboardingRequestGuid = response.value[0].onboardingRequestGuid;
    }

    @computedFrom(Reflection.getPropertyPath<ActivityAssignment>(z => z.recipients),
        Reflection.getPropertyPath<ActivityAssignment>(z => z.recipients.length))
    get orderedRecipients() {
        //sorts to put "To" emails at the top. TO has priority over CC and BCC due to the enum number. 
        //TO is 1, CC is 2 and BCC is 3
        //so when sorting, emails will be in that order 
        return Enumerable.from(this.recipients).orderBy(x => x.emailRecipientType).toArray();

    }

}